import type { UserRole } from '../types';

// ============================================================
// Permission Keys
// ============================================================

export type Permission =
  | 'access_command'        // Can use the desktop Command & Control system
  | 'access_mdt'            // Can use the MDT mobile system
  | 'manage_users'          // User administration (create/edit/disable accounts)
  | 'manage_system'         // System configuration & audit logs
  | 'manage_roles'          // Edit roles and permission assignments
  | 'create_incidents'      // Can create new incidents
  | 'manage_incidents'      // Can update status, close, reassign incidents
  | 'dispatch_units'        // Can dispatch/assign units to incidents
  | 'manage_resources'      // Can create, edit, decommission resources
  | 'view_resources'        // Can view the resources list (read-only)
  | 'view_analytics'        // Can view dashboard stats & analytics
  | 'view_all_incidents'    // Can see all incidents system-wide
  | 'request_assistance'    // Can trigger urgent assistance alert
  | 'manage_field_staff'    // Can override field staff status / force break
  | 'view_audit_log';       // Can view audit / activity logs

// ============================================================
// Role → Permission Mapping
// ============================================================

const rolePermissions: Record<UserRole, Permission[]> = {
  command_admin: [
    'access_command',
    'access_mdt',
    'manage_users',
    'manage_system',
    'manage_roles',
    'create_incidents',
    'manage_incidents',
    'dispatch_units',
    'manage_resources',
    'view_resources',
    'view_analytics',
    'view_all_incidents',
    'request_assistance',
    'manage_field_staff',
    'view_audit_log',
  ],
  command_supervisor: [
    'access_command',
    'access_mdt',
    'create_incidents',
    'manage_incidents',
    'dispatch_units',
    'manage_resources',
    'view_resources',
    'view_analytics',
    'view_all_incidents',
    'request_assistance',
    'manage_field_staff',
    'view_audit_log',
  ],
  command_dispatcher: [
    'access_command',
    'create_incidents',
    'manage_incidents',
    'dispatch_units',
    'view_resources',
    'view_analytics',
    'view_all_incidents',
    'request_assistance',
  ],
  mdt_supervisor: [
    'access_mdt',
    'view_resources',
    'view_all_incidents',
    'request_assistance',
    'manage_field_staff',
  ],
  mdt_officer: [
    'access_mdt',
    'request_assistance',
  ],
};

// ============================================================
// Helper Functions
// ============================================================

export function getPermissions(role: UserRole): Permission[] {
  return rolePermissions[role] || [];
}

export function hasPermission(role: UserRole | undefined, permission: Permission): boolean {
  if (!role) return false;
  return getPermissions(role).includes(permission);
}

export function hasAnyPermission(role: UserRole | undefined, permissions: Permission[]): boolean {
  if (!role) return false;
  const userPerms = getPermissions(role);
  return permissions.some((p) => userPerms.includes(p));
}

export function hasAllPermissions(role: UserRole | undefined, permissions: Permission[]): boolean {
  if (!role) return false;
  const userPerms = getPermissions(role);
  return permissions.every((p) => userPerms.includes(p));
}

export function canAccessCommand(role: UserRole | undefined): boolean {
  return hasPermission(role, 'access_command');
}

export function canAccessMDT(role: UserRole | undefined): boolean {
  return hasPermission(role, 'access_mdt');
}

export function isMDTOnly(role: UserRole | undefined): boolean {
  return canAccessMDT(role) && !canAccessCommand(role);
}

export function hasDualAccess(role: UserRole | undefined): boolean {
  return canAccessCommand(role) && canAccessMDT(role);
}

// ============================================================
// Role Display Info
// ============================================================

export interface RoleInfo {
  label: string;
  description: string;
  system: 'command' | 'mdt' | 'both';
}

export const roleInfo: Record<UserRole, RoleInfo> = {
  command_admin: {
    label: 'Command Admin',
    description: 'Full system access including user management, configuration, and all operational features.',
    system: 'both',
  },
  command_supervisor: {
    label: 'Command Supervisor',
    description: 'Full operational access to Command system. Can manage resources, dispatch units, and oversee field staff.',
    system: 'both',
  },
  command_dispatcher: {
    label: 'Command Dispatcher',
    description: 'Can create and manage incidents, dispatch units, and monitor operations from the Command system.',
    system: 'command',
  },
  mdt_supervisor: {
    label: 'MDT Supervisor',
    description: 'Field supervisor with MDT access. Can view resources, all incidents, and manage field staff status.',
    system: 'mdt',
  },
  mdt_officer: {
    label: 'MDT Field Officer',
    description: 'Standard field staff with MDT access. Can view assigned incidents, update status, and communicate.',
    system: 'mdt',
  },
};
